import { useState, useRef } from "react";
import { X, UploadCloud, FileSpreadsheet, Download, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../connector/api";

export default function ITBulkUploadModal({ isOpen, onClose, fetchEquipment }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  // Function to dynamically trigger template download
  const handleDownloadTemplate = () => {
    const headers = [
      "description",
      "category",
      "model",
      "serial_number",
      "cost",
      "date_of_purchase",
      "location",
      "responsible_person"
    ];
    
    const sampleRow = [
      "Sony Alpha A7 IV Camera",
      "CCTV Camera",
      "PTZ-PRO-V4",
      "SN-84920-X",
      "1249.99",
      "2026-03-15",
      "Perimeter Gate Floor 2",
      "1"
    ];

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), sampleRow.join(",")].join("\n");
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "it_equipment_upload_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Template download initiated!");
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      const extension = droppedFile.name.split('.').pop().toLowerCase();
      if (['csv', 'xlsx'].includes(extension)) {
        setFile(droppedFile);
      } else {
        toast.error("Invalid file format. Please upload a .csv or .xlsx file.");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      await api.post("it-equipment/bulk-upload/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Bulk dataset parsed and imported successfully!");
      setFile(null);
      onClose();
      if (fetchEquipment) fetchEquipment();
    } catch (err) {
      console.log(err.response?.data);
      toast.error("Failed to parse and upload asset batch");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center font-dynamic-title justify-center p-4 bg-slate-900/40 backdrop-blur-sm z-50 animate-fade-in">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl border border-slate-100 overflow-hidden transform transition-all animate-scale-in">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div>
            <h2 className="text-base font-bold text-slate-900">Bulk Import Registries</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Upload spreadsheets to provision inventory nodes efficiently.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors cursor-pointer"
            disabled={uploading}
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 text-xs">
          
          {/* Custom Template Interactive Download Area */}
          <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg border border-slate-200 text-[#002855] shadow-xs">
                <FileSpreadsheet size={16} />
              </div>
              <div className="space-y-0.5">
                <h4 className="font-bold text-slate-800">Missing an import standard?</h4>
                <p className="text-[11px] text-slate-400">Get our structured CSV template sheet maps.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg shadow-2xs transition-all cursor-pointer"
            >
              <Download size={13} />
              Template
            </button>
          </div>

          {/* Premium Drag & Drop Area */}
          <div
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
              isDragActive 
                ? "border-[#002855] bg-slate-50/80 scale-[0.99]" 
                : file 
                  ? "border-emerald-200 bg-emerald-50/10 hover:bg-emerald-50/20" 
                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/60"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv, .xlsx"
              onChange={handleFileChange}
              className="hidden"
            />

            {!file ? (
              <>
                <div className="p-3 bg-slate-50 rounded-full text-slate-400 mb-3 border border-slate-100">
                  <UploadCloud size={20} />
                </div>
                <p className="font-semibold text-slate-700 text-[13px]">
                  Drop import file here or <span className="text-[#002855] underline">browse files</span>
                </p>
                <p className="text-slate-400 mt-1">Accepts raw spreadsheet matrices formatted via CSV or XLSX.</p>
              </>
            ) : (
              <>
                <div className="p-3 bg-emerald-100/70 rounded-full text-emerald-600 mb-3 animate-bounce">
                  <FileSpreadsheet size={20} />
                </div>
                <p className="font-bold text-emerald-800 text-[13px] tracking-wide max-w-xs truncate">
                  {file.name}
                </p>
                <p className="text-emerald-500 mt-1 font-medium">
                  {parseFloat((file.size / 1024).toFixed(1))} KB · Ready to batch populate
                </p>
              </>
            )}
          </div>

          {/* Guidelines Banner */}
          <div className="flex gap-2.5 p-3 bg-amber-50/60 border border-amber-100/70 rounded-xl text-amber-800 text-[11px] leading-relaxed">
            <AlertCircle size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="font-bold block">Parsing Standard Verification Checklist:</span>
              <p className="text-amber-700/90 font-medium">
                Ensure processing header schema matches strings verbatim:
                <code className="block bg-white/70 px-1.5 py-0.5 rounded border border-amber-200/50 font-mono mt-1 font-bold text-slate-700 tracking-tight select-all">
                  description, category, model, serial_number, cost, date_of_purchase, location, responsible_person
                </code>
              </p>
            </div>
          </div>

          {/* Sub-Footer Controls */}
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 mountaineer font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-all cursor-pointer"
              disabled={uploading}
            >
              Cancel Operation
            </button>
            <button
              type="submit"
              className="px-5 py-2 font-bold uppercase tracking-wider text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl shadow-sm transition-all cursor-pointer disabled:bg-slate-200 disabled:text-slate-400"
              disabled={uploading || !file}
            >
              {uploading ? "Parsing Matrix..." : "Execute Bulk Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}